/**
 * System Monitoring - Interne Systemüberwachung
 * Überwacht DEXIOS-Komponenten, Logs und Performance
 */
import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Badge, Spinner, Alert, Table, Form, Button } from 'react-bootstrap';
import { getSystemHealth, getSystemMetrics, getServiceLogs, getRecentErrors } from '../../services/api';

function SystemMonitoring() {
  const [health, setHealth] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [logs, setLogs] = useState('');
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedService, setSelectedService] = useState('backend');
  const [logLines, setLogLines] = useState(100);
  const [loadingLogs, setLoadingLogs] = useState(false);

  useEffect(() => {
    loadData();
    // Auto-refresh alle 30 Sekunden
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      setError(null);
      const [healthRes, metricsRes, errorsRes] = await Promise.all([
        getSystemHealth(),
        getSystemMetrics(),
        getRecentErrors(24),
      ]);
      setHealth(healthRes.data);
      setMetrics(metricsRes.data);
      setErrors(errorsRes.data.errors || []);
    } catch (err) {
      console.error('Fehler beim Laden:', err);
      setError('Fehler beim Laden der System-Daten');
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async () => {
    setLoadingLogs(true);
    try {
      const response = await getServiceLogs(selectedService, logLines);
      setLogs(response.data.logs || '');
    } catch (err) {
      console.error('Fehler beim Laden der Logs:', err);
      setLogs(`Fehler beim Laden der Logs: ${err.message}`);
    } finally {
      setLoadingLogs(false);
    }
  };

  useEffect(() => {
    loadLogs();
  }, [selectedService, logLines]);

  const getStatusColor = (status) => {
    const colors = {
      up: 'success',
      down: 'danger',
      degraded: 'warning',
      error: 'danger',
      healthy: 'success',
      unhealthy: 'danger',
    };
    return colors[status] || 'secondary';
  };

  const getStatusIcon = (status) => {
    if (status === 'up' || status === 'healthy') return '✅';
    if (status === 'down' || status === 'unhealthy') return '❌';
    if (status === 'degraded') return '⚠️';
    return '❓';
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="success" className="spinner-pcs" />
        <p className="mt-3">Lade System-Daten...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2>🔧 System-Monitoring</h2>
          <p className="text-muted">Interne Überwachung der DEXIOS-Komponenten</p>
        </div>
        <Button variant="outline-success" onClick={loadData}>
          🔄 Aktualisieren
        </Button>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* System Health */}
      {health && (
        <Card className="mb-4">
          <Card.Header className="bg-dark text-white">
            <strong>System Health</strong>
            <Badge bg={getStatusColor(health.status)} className="ms-2">
              {getStatusIcon(health.status)} {health.status.toUpperCase()}
            </Badge>
          </Card.Header>
          <Card.Body>
            <Row>
              {Object.entries(health.components).map(([component, data]) => (
                <Col md={4} key={component} className="mb-3">
                  <Card className="h-100">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h5 className="mb-0">{component}</h5>
                        <Badge bg={getStatusColor(data.status)}>
                          {getStatusIcon(data.status)} {data.status}
                        </Badge>
                      </div>
                      <hr />
                      {data.message && <p className="mb-1"><small>{data.message}</small></p>}
                      {data.recent_checks !== undefined && (
                        <p className="mb-1"><small><strong>Recent Checks:</strong> {data.recent_checks}</small></p>
                      )}
                      {data.containers && (
                        <>
                          <p className="mb-1">
                            <small><strong>Container:</strong> {data.running} / {data.total} running</small>
                          </p>
                          <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                            {Object.entries(data.containers).map(([name, info]) => (
                              <div key={name} className="d-flex justify-content-between align-items-center mb-1">
                                <small>{name}</small>
                                <Badge bg={getStatusColor(info.status)} size="sm">
                                  {info.state}
                                </Badge>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* System Metrics */}
      {metrics && (
        <Card className="mb-4">
          <Card.Header className="bg-dark text-white">
            <strong>System Metriken</strong>
          </Card.Header>
          <Card.Body>
            <Row>
              {/* Endpoints */}
              <Col md={3} className="mb-3">
                <Card className="text-center">
                  <Card.Body>
                    <h3 className="text-pcs-primary">{metrics.endpoints.active}</h3>
                    <p className="mb-0"><strong>Aktive Endpoints</strong></p>
                    <small className="text-muted">{metrics.endpoints.total} gesamt</small>
                  </Card.Body>
                </Card>
              </Col>

              {/* Checks Today */}
              <Col md={3} className="mb-3">
                <Card className="text-center">
                  <Card.Body>
                    <h3 className="text-success">{metrics.checks.today}</h3>
                    <p className="mb-0"><strong>Checks Heute</strong></p>
                    <small className="text-muted">{metrics.checks.last_7_days} letzte 7 Tage</small>
                  </Card.Body>
                </Card>
              </Col>

              {/* Success Rate */}
              <Col md={3} className="mb-3">
                <Card className="text-center">
                  <Card.Body>
                    <h3 style={{ color: metrics.checks.success_rate_today >= 95 ? '#28a745' : '#ffc107' }}>
                      {metrics.checks.success_rate_today}%
                    </h3>
                    <p className="mb-0"><strong>Erfolgsrate</strong></p>
                    <small className="text-muted">{metrics.checks.failed_today} fehlgeschlagen</small>
                  </Card.Body>
                </Card>
              </Col>

              {/* Database Size */}
              <Col md={3} className="mb-3">
                <Card className="text-center">
                  <Card.Body>
                    <h3 className="text-info">{metrics.database.size}</h3>
                    <p className="mb-0"><strong>Datenbank-Größe</strong></p>
                    <small className="text-muted">{metrics.database.tables.length} Tabellen</small>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Database Tables */}
            <div className="mt-3">
              <h6>Datenbank-Tabellen</h6>
              <Table striped bordered hover size="sm">
                <thead>
                  <tr>
                    <th>Tabelle</th>
                    <th>Größe</th>
                  </tr>
                </thead>
                <tbody>
                  {metrics.database.tables.map((table) => (
                    <tr key={table.name}>
                      <td>{table.name}</td>
                      <td>{table.size}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Service Logs */}
      <Card className="mb-4">
        <Card.Header className="bg-dark text-white">
          <strong>Service Logs</strong>
        </Card.Header>
        <Card.Body>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label><strong>Service auswählen</strong></Form.Label>
                <Form.Select
                  value={selectedService}
                  onChange={(e) => setSelectedService(e.target.value)}
                >
                  <option value="backend">Backend (API)</option>
                  <option value="frontend">Frontend (React)</option>
                  <option value="worker">Worker (Health Checker)</option>
                  <option value="postgres">Database (PostgreSQL)</option>
                  <option value="redis">Redis</option>
                  <option value="nginx">Nginx</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label><strong>Anzahl Zeilen</strong></Form.Label>
                <Form.Select
                  value={logLines}
                  onChange={(e) => setLogLines(Number(e.target.value))}
                >
                  <option value="50">50</option>
                  <option value="100">100</option>
                  <option value="200">200</option>
                  <option value="500">500</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3} className="d-flex align-items-end">
              <Button variant="success" onClick={loadLogs} disabled={loadingLogs} className="w-100">
                {loadingLogs ? <Spinner animation="border" size="sm" /> : '🔄 Logs laden'}
              </Button>
            </Col>
          </Row>

          <div
            style={{
              backgroundColor: '#1e1e1e',
              color: '#d4d4d4',
              padding: '15px',
              borderRadius: '4px',
              maxHeight: '500px',
              overflowY: 'auto',
              fontFamily: 'monospace',
              fontSize: '12px',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
            }}
          >
            {logs || 'Keine Logs verfügbar'}
          </div>
        </Card.Body>
      </Card>

      {/* Recent Errors */}
      <Card className="mb-4">
        <Card.Header className="bg-danger text-white">
          <strong>Letzte Fehler (24h)</strong>
          <Badge bg="light" text="dark" className="ms-2">{errors.length}</Badge>
        </Card.Header>
        <Card.Body>
          {errors.length === 0 ? (
            <Alert variant="success">✅ Keine Fehler in den letzten 24 Stunden!</Alert>
          ) : (
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>Zeitpunkt</th>
                    <th>Endpoint</th>
                    <th>URL</th>
                    <th>Status</th>
                    <th>Fehler</th>
                  </tr>
                </thead>
                <tbody>
                  {errors.map((err) => (
                    <tr key={err.id}>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <small>{new Date(err.timestamp).toLocaleString('de-DE')}</small>
                      </td>
                      <td><strong>{err.endpoint_name}</strong></td>
                      <td><small>{err.endpoint_url}</small></td>
                      <td>
                        <Badge bg="danger">{err.status_code || 'N/A'}</Badge>
                      </td>
                      <td><small>{err.error_message}</small></td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Footer */}
      <div className="text-center text-muted mt-4 mb-4">
        <small>
          Letzte Aktualisierung: {new Date().toLocaleString('de-DE')} ·
          Auto-Refresh alle 30 Sekunden
        </small>
      </div>
    </div>
  );
}

export default SystemMonitoring;
